import ReactGA from 'react-ga4'

declare global {
    interface Window {
        fbq?: (...args: unknown[]) => void
        _fbq?: unknown
        dataLayer?: unknown[]
    }
}

// ─── Configuration ─────────────────────────────────────────────
const GA_MEASUREMENT_ID = 'G-7H6HYX20DX'
const META_PIXEL_ID = '997560832951265'
const CONSENT_KEY = 'barbermate_cookie_consent'
const UTM_KEY = 'barbermate_utm'
const UTM_TTL_DAYS = 30

const isDev = !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
const isLocalhost = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

// ─── Types ─────────────────────────────────────────────────────
export interface ConsentPreferences {
    analytics: boolean
    errorMonitoring: boolean
    timestamp: string
}

export interface UTMParams {
    source: string | null
    medium: string | null
    campaign: string | null
    term: string | null
    content: string | null
    capturedAt: string
}

export type UserRole = 'barber' | 'customer'
export type BookingStep = 1 | 2 | 3 | 4 | 5 | 6 | 7
export type BookingStepName = 'category' | 'service' | 'worker' | 'date' | 'timeslot' | 'details' | 'twoFA'

// ─── Consent state ─────────────────────────────────────────────
let gaInitialized = false
let metaPixelLoaded = false

function getStoredConsent(): ConsentPreferences | null {
    if (typeof window === 'undefined') return null
    try {
        const stored = localStorage.getItem(CONSENT_KEY)
        return stored ? JSON.parse(stored) : null
    } catch {
        return null
    }
}

export function hasAnalyticsConsent(): boolean {
    return getStoredConsent()?.analytics === true
}

// ─── SDK loaders ───────────────────────────────────────────────
function initGA() {
    if (gaInitialized || isLocalhost) return
    ReactGA.initialize(GA_MEASUREMENT_ID, {
        testMode: isDev,
    })
    gaInitialized = true
}

function loadMetaPixel() {
    if (metaPixelLoaded || isLocalhost || typeof window === 'undefined') return

    // Standard Meta Pixel snippet, adapted to TS
    /* eslint-disable */
    ;(function (f: any, b: Document, e: string, v: string) {
        if (f.fbq) return
        const n: any = (f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
        })
        if (!f._fbq) f._fbq = n
        n.push = n
        n.loaded = true
        n.version = '2.0'
        n.queue = []
        const t = b.createElement(e) as HTMLScriptElement
        t.async = true
        t.src = v
        const s = b.getElementsByTagName(e)[0]
        s.parentNode?.insertBefore(t, s)
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
    /* eslint-enable */

    window.fbq?.('init', META_PIXEL_ID)
    window.fbq?.('track', 'PageView')
    metaPixelLoaded = true
}

// ─── UTM capture ───────────────────────────────────────────────
export function captureUTMs() {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const source = params.get('utm_source')
    const medium = params.get('utm_medium')
    const campaign = params.get('utm_campaign')

    if (!source && !medium && !campaign) return

    const utm: UTMParams = {
        source,
        medium,
        campaign,
        term: params.get('utm_term'),
        content: params.get('utm_content'),
        capturedAt: new Date().toISOString(),
    }
    try {
        localStorage.setItem(UTM_KEY, JSON.stringify(utm))
    } catch {
        // localStorage full or disabled — ignore
    }
}

export function getUTMs(): UTMParams | null {
    if (typeof window === 'undefined') return null
    try {
        const raw = localStorage.getItem(UTM_KEY)
        if (!raw) return null
        const data: UTMParams = JSON.parse(raw)
        const ageMs = Date.now() - new Date(data.capturedAt).getTime()
        if (ageMs > UTM_TTL_DAYS * 24 * 3600 * 1000) {
            localStorage.removeItem(UTM_KEY)
            return null
        }
        return data
    } catch {
        return null
    }
}

function utmPayload(): Record<string, string> {
    const utm = getUTMs()
    if (!utm) return {}
    const out: Record<string, string> = {}
    if (utm.source) out.utm_source = utm.source
    if (utm.medium) out.utm_medium = utm.medium
    if (utm.campaign) out.utm_campaign = utm.campaign
    if (utm.term) out.utm_term = utm.term
    if (utm.content) out.utm_content = utm.content
    return out
}

// ─── Server-side analytics attribution ────────────────────────
// The `_ga` cookie is GA4's pseudo-user identifier. Backend uses it as
// `client_id` in Measurement Protocol calls so server events attach to the
// same GA4 user as browser events. Format: GA1.1.{random}.{timestamp}.
export function getGaClientId(): string | null {
    if (typeof document === 'undefined') return null
    const match = document.cookie.match(/_ga=GA\d\.\d\.([^;]+)/)
    return match ? match[1] : null
}

// `_fbp` is the Meta Pixel browser ID. `_fbc` is set when the user clicked
// a Facebook ad (contains fbclid). Both feed Meta CAPI user_data for dedupe
// with the browser Pixel.
export function getFbCookies(): { fbp: string | null; fbc: string | null } {
    if (typeof document === 'undefined') return { fbp: null, fbc: null }
    const fbp = document.cookie.match(/_fbp=([^;]+)/)
    const fbc = document.cookie.match(/_fbc=([^;]+)/)
    return {
        fbp: fbp ? fbp[1] : null,
        fbc: fbc ? fbc[1] : null,
    }
}

// Combined attribution payload to spread into checkout calls. All fields
// optional — backend treats nulls as "not available" and falls back to
// synthetic identifiers when needed.
export function getCheckoutAttribution(): Record<string, string | boolean> {
    const out: Record<string, string | boolean> = {}
    const ga = getGaClientId()
    if (ga) out.ga_client_id = ga
    const { fbp, fbc } = getFbCookies()
    if (fbp) out.fbp = fbp
    if (fbc) out.fbc = fbc
    const stored = getStoredConsent()
    if (stored) out.analytics_consent = stored.analytics === true
    return { ...out, ...utmPayload() }
}

// ─── Tracking primitives ───────────────────────────────────────
function shouldTrack(): boolean {
    if (isDev || isLocalhost) return false
    return hasAnalyticsConsent()
}

function gaEvent(action: string, category: string, label?: string, value?: number, extra?: Record<string, unknown>) {
    if (!shouldTrack() || !gaInitialized) return
    // GA4 prefers event-based tracking with custom params
    ReactGA.event(action, {
        event_category: category,
        event_label: label,
        value,
        ...utmPayload(),
        ...(extra ?? {}),
    })
}

function fbqTrack(eventName: string, params?: Record<string, unknown>) {
    if (!shouldTrack()) return
    window.fbq?.('track', eventName, params)
}

function fbqTrackCustom(eventName: string, params?: Record<string, unknown>) {
    if (!shouldTrack()) return
    window.fbq?.('trackCustom', eventName, params)
}

// ─── Public API ────────────────────────────────────────────────
export const analytics = {
    // ============ CONSENT / SDK INIT ============
    /**
     * Called on app boot. Sets up gtag consent defaults (denied) and prepares
     * the SDK state. If consent has already been granted in a previous session,
     * SDKs are loaded immediately.
     */
    bootstrap() {
        if (typeof window === 'undefined' || isLocalhost) return

        // gtag consent defaults (must be set before GA loads anything)
        window.dataLayer = window.dataLayer || []
        if (!window.gtag) {
            window.gtag = function (...args: unknown[]) {
                window.dataLayer?.push(args)
            }
        }
        window.gtag('consent', 'default', {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            wait_for_update: 500,
        })

        // Capture UTMs from URL on every load (newer overwrites)
        captureUTMs()

        // Initialize GA (it will respect consent default of 'denied')
        initGA()

        // If user previously consented, enable everything
        if (hasAnalyticsConsent()) {
            this.enableTracking()
        }
    },

    /**
     * Called by CookieConsent when user accepts analytics.
     * Updates gtag consent and loads Meta Pixel.
     */
    enableTracking() {
        if (isLocalhost) return
        if (typeof window.gtag === 'function') {
            window.gtag('consent', 'update', {
                analytics_storage: 'granted',
                ad_storage: 'granted',
                ad_user_data: 'granted',
                ad_personalization: 'granted',
            })
        }
        // Init GA if it hasn't been yet
        initGA()
        loadMetaPixel()
    },

    /**
     * Called by CookieConsent when user denies analytics.
     */
    disableTracking() {
        if (typeof window.gtag === 'function') {
            window.gtag('consent', 'update', {
                analytics_storage: 'denied',
                ad_storage: 'denied',
                ad_user_data: 'denied',
                ad_personalization: 'denied',
            })
        }
    },

    /**
     * Sets the GA4 user_id and user_properties when a user logs in.
     */
    setUser(userId: string, role: UserRole, properties?: Record<string, unknown>) {
        if (!shouldTrack() || !gaInitialized) return
        ReactGA.set({user_id: userId})
        ReactGA.gtag('set', 'user_properties', {
            role,
            ...properties,
        })
    },

    clearUser() {
        if (!gaInitialized) return
        ReactGA.set({user_id: null})
    },

    pageview(path: string) {
        if (!shouldTrack() || !gaInitialized) return
        ReactGA.send({hitType: 'pageview', page: path})
    },

    // ============ REGISTRATION ============
    signupStarted(role: UserRole) {
        gaEvent('signup_started', 'Registration', role)
        fbqTrack('Lead', {content_name: `${role}_signup`})
    },
    signupCompleted(role: UserRole) {
        gaEvent('signup_completed', 'Registration', role)
        fbqTrack('CompleteRegistration', {content_name: role})
    },
    signupFailed(role: UserRole, reason: string) {
        gaEvent('signup_failed', 'Registration', `${role}_${reason}`)
    },

    // ============ AUTH ============
    loginSucceeded(role: UserRole) {
        gaEvent('login', 'Auth', role)
        fbqTrackCustom('Login', {role})
    },
    loginFailed(role: UserRole, reason: string) {
        gaEvent('login_failed', 'Auth', `${role}_${reason}`)
    },
    logout(role: UserRole) {
        gaEvent('logout', 'Auth', role)
    },

    // ============ BOOKING FUNNEL ============
    shopViewed(shopSlug: string) {
        gaEvent('shop_viewed', 'Discovery', shopSlug)
        fbqTrack('ViewContent', {content_name: shopSlug, content_category: 'barbershop'})
    },
    bookingStarted(shopSlug: string) {
        gaEvent('booking_started', 'Booking', shopSlug)
        fbqTrack('InitiateCheckout', {content_name: shopSlug, content_category: 'booking'})
    },
    bookingStepViewed(step: BookingStep, stepName: BookingStepName, shopSlug: string) {
        gaEvent('booking_step_viewed', 'Booking', `${step}_${stepName}`, undefined, {
            step,
            step_name: stepName,
            shop: shopSlug,
        })
    },
    serviceSelected(serviceId: string, shopSlug: string) {
        gaEvent('booking_service_selected', 'Booking', shopSlug, undefined, {service_id: serviceId})
    },
    workerSelected(workerId: string, shopSlug: string) {
        gaEvent('booking_worker_selected', 'Booking', shopSlug, undefined, {worker_id: workerId})
    },
    dateSelected(date: string, shopSlug: string) {
        gaEvent('booking_date_selected', 'Booking', shopSlug, undefined, {date})
    },
    timeslotSelected(timeSlot: string, shopSlug: string) {
        gaEvent('booking_timeslot_selected', 'Booking', shopSlug, undefined, {time_slot: timeSlot})
    },
    bookingCompleted(shopSlug: string, value?: number) {
        gaEvent('booking_completed', 'Booking', shopSlug, value)
        // GA4 e-commerce style purchase event for revenue reporting
        if (gaInitialized && shouldTrack()) {
            ReactGA.gtag('event', 'purchase', {
                value: value ?? 0,
                currency: 'EUR',
                transaction_id: `booking_${Date.now()}`,
                items: [{item_name: 'haircut', item_category: 'barbershop', item_brand: shopSlug}],
                ...utmPayload(),
            })
        }
        fbqTrack('Schedule', {content_name: shopSlug, value, currency: 'EUR'})
    },
    bookingAbandoned(lastStep: BookingStep, lastStepName: BookingStepName, shopSlug: string) {
        gaEvent('booking_abandoned', 'Booking', `${lastStep}_${lastStepName}`, undefined, {
            last_step: lastStep,
            last_step_name: lastStepName,
            shop: shopSlug,
        })
    },
    bookingFailed(shopSlug: string, reason: string) {
        gaEvent('booking_failed', 'Booking', `${shopSlug}_${reason}`)
    },

    // ============ EXPLORE / DISCOVERY ============
    exploreViewed() {
        gaEvent('explore_viewed', 'Discovery')
        fbqTrack('Search')
    },
    giftPurchaseViewed() {
        gaEvent('gift_purchase_viewed', 'Gift')
    },
    searchFilterApplied(filter: string, value: string | number | boolean) {
        gaEvent('search_filter_applied', 'Discovery', filter, undefined, {filter, value: String(value)})
    },
    zeroResultsReturned(query: string, filterCount: number) {
        gaEvent('zero_results_returned', 'Discovery', query, undefined, {
            query,
            filter_count: filterCount,
        })
    },
    shopCardClicked(shopSlug: string, position: number, source: 'explore' | 'landing' | 'search' | 'recommended') {
        gaEvent('shop_card_clicked', 'Discovery', shopSlug, undefined, {position, source})
        fbqTrack('ViewContent', {content_name: shopSlug, source})
    },
    geolocationGranted() {
        gaEvent('geolocation_granted', 'Discovery')
    },
    geolocationDenied(reason: string) {
        gaEvent('geolocation_denied', 'Discovery', reason)
    },

    // ============ PRICING / CHECKOUT ============
    pricingViewed() {
        gaEvent('pricing_viewed', 'Conversion')
        fbqTrackCustom('PricingViewed')
    },
    planSelected(plan: string) {
        gaEvent('plan_selected', 'Conversion', plan)
        fbqTrack('AddToCart', {content_name: plan, content_category: 'plan'})
    },
    checkoutStarted(plan: string, billingInterval: 'monthly' | 'yearly') {
        gaEvent('checkout_started', 'Conversion', plan, undefined, {plan, billing_interval: billingInterval})
        fbqTrack('InitiateCheckout', {content_name: plan, content_category: 'plan'})
    },
    checkoutCompleted(plan: string, value: number, billingInterval?: 'monthly' | 'yearly') {
        gaEvent('checkout_completed', 'Conversion', plan, value, {plan, billing_interval: billingInterval})
        if (gaInitialized && shouldTrack()) {
            ReactGA.gtag('event', 'purchase', {
                value,
                currency: 'EUR',
                transaction_id: `subscription_${Date.now()}`,
                items: [{item_name: plan, item_category: 'subscription'}],
                ...utmPayload(),
            })
        }
        fbqTrack('Subscribe', {value, currency: 'EUR', content_name: plan, predicted_ltv: value * 12})
    },
    checkoutFailed(plan: string, reason: string) {
        gaEvent('checkout_failed', 'Conversion', `${plan}_${reason}`)
    },

    // ============ SUBSCRIPTION ============
    subscriptionCancelled(plan: string, daysActive?: number) {
        gaEvent('subscription_cancelled', 'Subscription', plan, daysActive, {plan, days_active: daysActive})
        fbqTrackCustom('SubscriptionCancelled', {plan, days_active: daysActive})
    },
    subscriptionDowngraded(fromPlan: string, toPlan: string) {
        gaEvent('subscription_downgraded', 'Subscription', `${fromPlan}_to_${toPlan}`, undefined, {
            from_plan: fromPlan,
            to_plan: toPlan,
        })
    },
    subscriptionPortalOpened() {
        gaEvent('subscription_portal_opened', 'Subscription')
    },

    // ============ RATING ============
    ratingStarted(barberShopSlug?: string) {
        gaEvent('rating_started', 'Rating', barberShopSlug)
    },
    ratingSubmitted(stars: number, hasText: boolean, hasPhoto: boolean, barberShopSlug?: string) {
        gaEvent('rating_submitted', 'Rating', barberShopSlug, stars, {
            stars,
            has_text: hasText,
            has_photo: hasPhoto,
        })
    },
    tipPromptShown(barberShopSlug?: string) {
        gaEvent('tip_prompt_shown', 'Rating', barberShopSlug)
    },
    tipCheckoutStarted(amountEur: number, presetPct: number | null, barberShopSlug?: string) {
        gaEvent('tip_checkout_started', 'Rating', barberShopSlug, amountEur, {
            amount: amountEur,
            preset_pct: presetPct ?? 'custom',
        })
    },

    // ============ BARBER BACK-OFFICE ============
    calendarEventCreated(eventType: 'reservation' | 'vacation' | 'block') {
        gaEvent('calendar_event_created', 'Backoffice', eventType)
    },
    calendarEventEdited(eventType: 'reservation' | 'vacation' | 'block') {
        gaEvent('calendar_event_edited', 'Backoffice', eventType)
    },
    calendarEventDeleted(eventType: 'reservation' | 'vacation' | 'block') {
        gaEvent('calendar_event_deleted', 'Backoffice', eventType)
    },
    vacationCreated() {
        gaEvent('vacation_created', 'Backoffice')
    },
    vacationDeleted() {
        gaEvent('vacation_deleted', 'Backoffice')
    },
    settingsChanged(setting: string, value?: string | number | boolean) {
        gaEvent('settings_changed', 'Backoffice', setting, undefined, {
            setting,
            ...(value !== undefined ? {new_value: String(value)} : {}),
        })
    },
    backofficeViewed(area: 'calendar' | 'reservations' | 'vacations' | 'settings' | 'reviews' | 'customers' | 'dashboard' | 'notifications' | 'plan_of_day' | 'kiosk' | 'insights' | 'admin_dashboard' | 'gift_cards') {
        gaEvent('backoffice_viewed', 'Backoffice', area)
    },

    // ============ FORM ERRORS ============
    formError(form: string, field: string, errorType: string) {
        gaEvent('form_error', 'Forms', `${form}_${field}_${errorType}`, undefined, {
            form,
            field,
            error_type: errorType,
        })
    },

    // ============ WEB VITALS ============
    webVital(name: string, value: number, rating: 'good' | 'needs-improvement' | 'poor', id?: string) {
        gaEvent('web_vitals', 'Performance', name, Math.round(name === 'CLS' ? value * 1000 : value), {
            metric_name: name,
            metric_value: value,
            metric_rating: rating,
            metric_id: id,
        })
    },

    // ============ WAITLIST ============
    waitlistJoinStarted(shopSlug: string) {
        gaEvent('waitlist_join_started', 'Waitlist', shopSlug)
    },
    waitlistJoinCompleted(shopSlug: string, mode: 'strict' | 'time_of_day' | 'daily', isPriority: boolean) {
        gaEvent('waitlist_join_completed', 'Waitlist', shopSlug, undefined, {
            mode,
            is_priority: isPriority,
        })
        fbqTrackCustom('WaitlistJoin', {shop: shopSlug, mode, is_priority: isPriority})
    },
    waitlistJoinFailed(shopSlug: string, reason: string) {
        gaEvent('waitlist_join_failed', 'Waitlist', `${shopSlug}_${reason}`)
    },
    waitlistCancelled(shopSlug: string, reason?: string) {
        gaEvent('waitlist_cancelled', 'Waitlist', shopSlug, undefined, {reason: reason ?? 'unspecified'})
    },
    waitlistViewed(area: 'customer' | 'barber') {
        gaEvent('waitlist_viewed', 'Waitlist', area)
    },
    waitlistFulfilled(shopSlug: string) {
        gaEvent('waitlist_fulfilled', 'Waitlist', shopSlug)
    },
    priorityPassCheckoutStarted(shopSlug: string, source: 'join_modal' | 'upgrade_button') {
        gaEvent('priority_pass_checkout_started', 'Waitlist', shopSlug, undefined, {source})
        fbqTrack('InitiateCheckout', {content_name: 'priority_pass', shop: shopSlug, source})
    },
    priorityPassPurchased(shopSlug: string, amount: number) {
        // Server fires the real `purchase` event from the Stripe webhook (revenue truth).
        // FE event is best-effort: it only fires when the user lands back on the success URL,
        // which adblockers / closed-tab scenarios will skip. Keep both, dedupe server-side.
        gaEvent('priority_pass_purchased', 'Waitlist', shopSlug, amount)
        fbqTrack('Purchase', {value: amount, currency: 'EUR', content_name: 'priority_pass'})
    },
    priorityPassRefunded(shopSlug: string, reason: string) {
        gaEvent('priority_pass_refunded', 'Waitlist', `${shopSlug}_${reason}`, undefined, {reason})
    },
    priorityPassCheckoutCancelled(shopSlug: string) {
        gaEvent('priority_pass_checkout_cancelled', 'Waitlist', shopSlug)
    },

    // ============ GENERIC ============
    ctaClicked(location: string, target: string) {
        gaEvent('cta_clicked', 'CTA', `${location}_${target}`, undefined, {location, target})
        fbqTrackCustom('CTAClicked', {location, target})
    },
    contactClicked(method: string) {
        gaEvent('contact_clicked', 'Contact', method)
        fbqTrack('Contact', {content_name: method})
    },

    // ============ NOTIFICATIONS SETTINGS ============
    notificationsSmsMasterToggled(state: 'enabled' | 'disabled') {
        gaEvent('notifications_sms_master_toggled', 'NotificationsSettings', state)
    },
    notificationsPresetChanged(preset: string) {
        gaEvent('notifications_preset_changed', 'NotificationsSettings', preset)
    },

    // ============ STALE BUILD / VERSION ============
    versionPromptShown(currentSha: string, latestSha: string) {
        gaEvent('update_prompt_shown', 'StaleBuild', latestSha, undefined, {
            current_sha: currentSha,
            latest_sha: latestSha,
        })
    },
    versionPromptAccepted(currentSha: string, latestSha: string, stalenessMs: number, source: 'toast' | 'banner') {
        gaEvent('update_prompt_accepted', 'StaleBuild', latestSha, stalenessMs, {
            current_sha: currentSha,
            latest_sha: latestSha,
            staleness_ms: stalenessMs,
            source,
        })
    },
    versionPromptDismissed(currentSha: string, latestSha: string, stalenessMs: number) {
        gaEvent('update_prompt_dismissed', 'StaleBuild', latestSha, stalenessMs, {
            current_sha: currentSha,
            latest_sha: latestSha,
            staleness_ms: stalenessMs,
        })
    },
}